import React, { useMemo } from "react";
import { OccupationTypes } from "../../../_enums/occipationTypes.enum";
import { CandidateInterface } from "../../../_types/candidate";
import { EventTargetType } from "../../../_types/form";
import { CandidateSexItems, employmentPreferenceItems, locationItems, PronounTypes, subjectToNoticeItems, subsidiarySubjectItems, TitleTypes, TransportItems } from "../../../config/formData";
import { CandidateEmailHelpText, CandidateFirstNameHelpText, CandidateLastNameHelpText, TRNLastCheckedDateHelpText } from "../../../config/helpText";
import { IsNonUk, ShowFormQuestion } from "../../../functions/helpers";
import { useCustomerOccupations } from "../../../hooks/useCustomerOccupations";
import { FormQuestionModuleType, FormQuestionType, useFormQuestions } from "../../../state/context/formQuestions.store";
import { AlertType } from "../../../state/reducers/setAlert";
import { FormSubForm, FormSubFormTitle } from "../../../styles/forms";
import Col from "../../../styles/layout/col";
import Row from "../../../styles/layout/row";
import { VoipButton } from "../../buttons/voipButton";
import PhoneButtonIcon from "../../icons/phoneIcon";
import HelpModal from "../../modals/help.modal";
import Address from "../inputs/address";
import AddressCountryInput from "../inputs/country";
import DateInput from "../inputs/date";
import MultiSelectInput from "../inputs/multiSelect";
import Select from "../inputs/select";
import Text from "../inputs/text";
import TextareaInput from "../inputs/textarea";
import ToggleInput from "../inputs/toggle";
import { useUserInfo } from "../../../state/context/userInfo.store";
import PhoneTextInput from "../inputs/phoneNumberInput";

export const teachingOccupations = new Set([
	OccupationTypes.Teacher,
	OccupationTypes.PrimaryTeacher,
	OccupationTypes.SecondaryTeacher,
	OccupationTypes.HeadTeacher,
	OccupationTypes.NurtureTeacher,
	OccupationTypes.SENDCo,
	OccupationTypes.ExecutiveHeadteacher,
	OccupationTypes.OverseasTrainedTeacher,
]);

type Props = {
	state: CandidateInterface;
	handleChange: (e: EventTargetType) => void;
	candidate: CandidateInterface;
	readOnly: boolean;
	addressKey: number;
	setAlert: (alert: AlertType) => void;
};

const ContactInformation = React.memo(({ state, handleChange, candidate, readOnly, addressKey, setAlert }: Props) => {
	const { formQuestions } = useFormQuestions();
	const { occupationOptions } = useCustomerOccupations();
	const { voipProvider, isLoading: voipProviderLoading } = useUserInfo();

	const shouldShowTRNRelatedQuestions = useMemo(() => {
		const isTeachingRelated = state.occupation && (teachingOccupations.has(state.occupation) || state.occupation.toLowerCase().includes("teach"));
		const shouldShowMedicalRequirements = ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.onboardingMedicalRequirements);
		const shouldAlwaysShowTrn = ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.showTrnNumberField);
		return isTeachingRelated || shouldShowMedicalRequirements || shouldAlwaysShowTrn;
	}, [state.occupation, formQuestions]);

	const customFieldMap = useMemo(
		() => ({
			CustomFieldAvailabilityLastChecked: ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.AvailabilityLastChecked),
			CustomFieldSubsidiarySubject: ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.SubsidiarySubject),
			CustomFieldSubjectToNotice: ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.SubjectToNotice),
			CustomFieldEmploymentPreference: ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.EmploymentPreference),
			CustomFieldOtherNames: ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.OtherNames),
		}),
		[formQuestions]
	);

	const { CustomFieldAvailabilityLastChecked, CustomFieldSubsidiarySubject, CustomFieldSubjectToNotice, CustomFieldEmploymentPreference, CustomFieldOtherNames } = customFieldMap;

	const hasACustomField = useMemo(() => Object.values(customFieldMap).some(Boolean), [customFieldMap]);

	return (
		<>
			<Row>
				<Col span={4} span1024={6} span768={12}>
					<Text
						name="forename"
						label="Candidate First Name"
						value={state.forename}
						onChange={handleChange}
						error={state.forenameError}
						required
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						help={{
							modalTitle: "Candidate First Name",
							modalContent: <HelpModal title="Candidate First Name" content={CandidateFirstNameHelpText} />,
						}}
						maxLength={255}
					/>
				</Col>
				<Col span={4} span1024={6} span768={12}>
					<Text
						name="surname"
						label="Candidate Last Name"
						value={state.surname}
						onChange={handleChange}
						error={state.surnameError}
						required
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						help={{
							modalTitle: "Candidate Last Name",
							modalContent: <HelpModal title="Candidate Last Name" content={CandidateLastNameHelpText} />,
						}}
						maxLength={255}
					/>
				</Col>
				<Col span={4} span1024={6} span768={12}>
					<Text
						name="previousNames"
						label="Candidate Previous Names"
						value={state.previousNames}
						onChange={handleChange}
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						maxLength={255}
					/>
				</Col>
			</Row>
			<Row>
				<Col span={4} span1024={6} span768={12}>
					<Text
						name="email"
						label="Email"
						type="email"
						value={state.email}
						onChange={handleChange}
						error={state.emailError}
						required
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						help={{
							modalTitle: "Candidate Email",
							modalContent: <HelpModal title="Candidate Email" content={CandidateEmailHelpText} />,
						}}
						maxLength={255}
					/>
				</Col>
				<Col span={4} span1024={6} span768={12}>
					<PhoneTextInput
						name="telephone"
						label={
							<>
								Home Telephone
								<VoipButton provider={voipProvider} entityType="candidate" entityId={candidate?._id} contactFieldToUse="telephone" setAlert={setAlert} isLoading={voipProviderLoading}>
									<PhoneButtonIcon />
								</VoipButton>
							</>
						}
						value={state.telephone}
						onChange={handleChange}
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						error={state.telephoneError}
					/>
				</Col>
				<Col span={4} span1024={6} span768={12}>
					<PhoneTextInput
						name="mobile"
						label={
							<>
								Mobile
								<VoipButton provider={voipProvider} entityType="candidate" entityId={candidate?._id} contactFieldToUse="mobile" setAlert={setAlert} isLoading={voipProviderLoading}>
									<PhoneButtonIcon />
								</VoipButton>
							</>
						}
						value={state.mobile}
						onChange={handleChange}
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						error={state.mobileError}
					/>
				</Col>
			</Row>
			<Row>
				{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.candidateTitle) ? (
					<Col span={4} span768={12}>
						<Select
							name="title"
							label="Title"
							value={state.title}
							onChange={handleChange}
							options={TitleTypes}
							error={state.titleError}
							readonly={!!candidate}
							canEditReadOnly={!readOnly}
						/>
					</Col>
				) : null}
				<Col span={4} span768={12} offsetLeft768={0}>
					<DateInput name="dob" label="Date of Birth" value={state.dob} onChange={handleChange} readonly={!!candidate} canEditReadOnly={!readOnly} />
				</Col>
				<Col span={4} span768={12} offsetLeft768={0}>
					<Select name="sex" label="Gender" value={state.sex} onChange={handleChange} options={CandidateSexItems} readonly={!!candidate} canEditReadOnly={!readOnly} />
				</Col>
				{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.candidatePronoun) ? (
					<Col span={4} span768={12}>
						<Select name="pronoun" label="Pronoun" value={state.pronoun} onChange={handleChange} options={PronounTypes} readonly={!!candidate} canEditReadOnly={!readOnly} />
					</Col>
				) : null}
			</Row>
			{!IsNonUk(formQuestions) ? (
				<>
					<Address
						key={addressKey}
						name="address"
						value={state.address}
						onChange={handleChange}
						addressLine1Error={state.addressLine1Error}
						postcodeError={state.postcodeError}
						readonly={!!candidate}
						canEditReadOnly={!readOnly}
						controlledUpdated
					/>
				</>
			) : (
				<>
					<AddressCountryInput name="address" value={state.address} onChange={handleChange} countryError={state.countryError} readonly={!!candidate} canEditReadOnly={!readOnly} />
				</>
			)}
			<Row>
				<Col span={4} span768={12}>
					{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.DesiredLocationMultiple) ? (
						<MultiSelectInput
							name="desiredLocationMultiple"
							label="Desired Locations"
							value={Array.isArray(state.desiredLocationMultiple) ? state.desiredLocationMultiple : []}
							onChange={handleChange}
							options={locationItems.map((item) => ({
								ID: item.ID,
								Name: item.Name,
							}))}
							readonly={readOnly}
							includeSearch
						/>
					) : (
						<Text
							name="desiredLocation"
							label="Desired Location"
							value={state.desiredLocation}
							onChange={handleChange}
							readonly={!!candidate}
							canEditReadOnly={!readOnly}
							maxLength={255}
						/>
					)}
				</Col>
			</Row>
			<Row>
				<Col span={4} span768={12}>
					<Select label="Occupation" name="occupation" value={state.occupation} onChange={handleChange} options={occupationOptions} readonly={!!candidate} canEditReadOnly={!readOnly} />
				</Col>
				<Col span={8} span768={12}>
					<Text name="subject" label="Specialism" value={state.subject} onChange={handleChange} readonly={!!candidate} canEditReadOnly={!readOnly} maxLength={255} />
				</Col>
			</Row>
			{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.onboardingTeacherPayScaleInput) &&
			(state.occupation === OccupationTypes.Teacher || state.occupation === OccupationTypes.PrimaryTeacher || state.occupation === OccupationTypes.SecondaryTeacher) ? (
				<Row>
					<Col span={4} span768={12}>
						<Text name="teacherPayScale" label="Pay Scale" value={state.teacherPayScale} onChange={handleChange} readonly={!!candidate} canEditReadOnly={!readOnly} maxLength={255} />
					</Col>
				</Row>
			) : null}
			<Row>
				{!IsNonUk(formQuestions) && (
					<>
						<Col span={4} span768={12}>
							{shouldShowTRNRelatedQuestions ? (
								<Text
									name="trnNumber"
									label={
										ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.onboardingMedicalRequirements)
											? "Candidate Registration Number"
											: "Teacher Registration Number"
									}
									note="If applicable"
									value={state.trnNumber}
									onChange={handleChange}
									readonly={!!candidate}
									canEditReadOnly={!readOnly}
									maxLength={50}
								/>
							) : null}
							<Select label="Transport" name="transport" value={state.transport} onChange={handleChange} options={TransportItems} readonly={!!candidate} canEditReadOnly={!readOnly} />
						</Col>
						{shouldShowTRNRelatedQuestions ? (
							<Col span={4} span768={12}>
								<DateInput
									name="trnNumberCheckedDate"
									label="TRN Last Checked"
									note="If applicable"
									value={state.trnNumberCheckedDate}
									onChange={handleChange}
									readonly={!!candidate}
									canEditReadOnly={!readOnly}
									help={{
										modalTitle: "TRN Last Checked",
										modalContent: <HelpModal title="TRN Last Checked" content={TRNLastCheckedDateHelpText} />,
									}}
								/>
								{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.showTrnNumberNotes) ? (
									<Text
										name="trnNumberNotes"
										label="TRN Notes"
										note="If applicable"
										value={state.trnNumberNotes}
										onChange={handleChange}
										readonly={!!candidate}
										canEditReadOnly={!readOnly}
										maxLength={150}
									/>
								) : null}
							</Col>
						) : null}
					</>
				)}
				{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.homeOfficeShareCode) && (
					<>
						<Col span={4} span768={12}>
							<Text
								name="homeOfficeShareCode"
								label="Home Office Share Code"
								note="If applicable"
								value={state.homeOfficeShareCode}
								onChange={handleChange}
								maxLength={12}
								readonly={!!candidate}
								canEditReadOnly={!readOnly}
							/>
						</Col>
					</>
				)}
				{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.bookingConfirmationEwcConfirmation) && (
					<>
						<Col span={4} span768={12}>
							<ToggleInput
								name="ewcConfirmation"
								label="Are you registered with the EWC?"
								note="If applicable"
								value={state.ewcConfirmation}
								onChange={handleChange}
								readonly={!!candidate}
								canEditReadOnly={!readOnly}
							/>
						</Col>
					</>
				)}
				{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.recordBoxNotes) && (
					<Col span={8} span1024={12}>
						<TextareaInput
							name="importantNotes"
							label="Important Notes"
							note="Internal Only"
							value={state.importantNotes}
							onChange={handleChange}
							readonly={!!candidate}
							canEditReadOnly={!readOnly}
							small
							max={300}
						/>
					</Col>
				)}
			</Row>
			{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.candidateEmploymentStartDate) && (
				<Row>
					<Col span={4} span1024={12}>
						<DateInput name="employmentStartDate" label="Employment Start Date" value={state.employmentStartDate} onChange={handleChange} />
					</Col>
				</Row>
			)}
			{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.onlineCheckConsent) && (
				<Row>
					<Col span={4} span1024={12}>
						<ToggleInput name="onlineCheckConsent" label="Consent to online check" value={state.onlineCheckConsent} onChange={handleChange} error={state.onlineCheckConsentError} />
					</Col>
				</Row>
			)}
			{hasACustomField && (
				<>
					<FormSubForm>
						<FormSubFormTitle>Custom Fields</FormSubFormTitle>
						<Row>
							{CustomFieldAvailabilityLastChecked && (
								<Col span={4} span768={12}>
									<DateInput
										name="availabilityLastChecked"
										label="Availability Last Checked"
										value={state.availabilityLastChecked}
										onChange={handleChange}
										readonly={!!candidate}
										canEditReadOnly={!readOnly}
									/>
								</Col>
							)}
							{CustomFieldSubsidiarySubject && (
								<Col span={4} span768={12}>
									<MultiSelectInput
										name="subsidiarySubject"
										label="Subsidiary Subject"
										value={Array.isArray(state.subsidiarySubject) ? state.subsidiarySubject : []}
										onChange={handleChange}
										options={subsidiarySubjectItems}
										includeSearch
									/>
								</Col>
							)}
							{CustomFieldSubjectToNotice && (
								<Col span={4} span768={12}>
									<Select
										name="subjectToNotice"
										label="Subject To Notice"
										value={state.subjectToNotice}
										onChange={handleChange}
										options={subjectToNoticeItems}
										readonly={readOnly}
										canEditReadOnly={!readOnly}
										includeSearch
									/>
								</Col>
							)}
							{CustomFieldEmploymentPreference && (
								<Col span={4} span768={12}>
									<MultiSelectInput
										name="employmentPreference"
										label="Employment Preference"
										value={state.employmentPreference}
										onChange={handleChange}
										options={employmentPreferenceItems}
										includeSearch
									/>
								</Col>
							)}
							{CustomFieldOtherNames && (
								<>
									<Col span={4} span768={12}>
										<Text
											name="otherNames"
											label="Other Names"
											value={state.otherNames}
											onChange={handleChange}
											readonly={!!candidate}
											canEditReadOnly={!readOnly}
											maxLength={255}
										/>
									</Col>
								</>
							)}
						</Row>
					</FormSubForm>
				</>
			)}
		</>
	);
});

ContactInformation.displayName = "ContactInformation";

export default ContactInformation;
